import { useState } from 'react';
import { FaSearch, FaFilter } from 'react-icons/fa';
import { Link } from 'react-router-dom';

type Course = {
  id: number;
  title: string;
  description: string;
  category: string;
  image: string;
  topicsLabel: string;
  topics: string[];
  projects?: string[];
};

const getMathTopics = (grade: number) => {
  if (grade <= 1) {
    return ['Number sense', 'Addition & subtraction', 'Shapes & measurement'];
  }

  if (grade <= 2) {
    return ['Place value', 'Addition & subtraction strategies', 'Measurement (length, time, money)'];
  }

  if (grade <= 3) {
    return ['Multiplication & division (intro)', 'Fractions (basic)', 'Geometry (perimeter, shapes)'];
  }

  if (grade <= 4) {
    return ['Multi-digit multiplication & division', 'Fractions & decimals (intro)', 'Measurement & angles'];
  }

  if (grade <= 5) {
    return ['Fractions & decimals (operations)', 'Data management (graphs, mean)', 'Geometry (area, volume intro)'];
  }

  if (grade <= 6) {
    return ['Ratios & percent (intro)', 'Integers & algebraic thinking', 'Area, volume & transformations'];
  }

  if (grade <= 7) {
    return ['Integers & rational numbers', 'Algebraic expressions & equations', 'Probability & statistics'];
  }

  if (grade <= 8) {
    return ['Linear relationships & graphing', 'Pythagorean theorem', 'Geometry & transformations'];
  }

  if (grade <= 10) {
    return ['Algebra', 'Trigonometry basics', 'Functions'];
  }

  return ['Advanced functions', 'Calculus basics', 'Statistics & probability'];
};

const mathematicsCourses: Course[] = Array.from({ length: 12 }, (_, i) => {
  const grade = i + 1;
  return {
    id: 1000 + grade,
    title: `Mathematics - Grade ${grade}`,
    description: `Mathematics program for Grade ${grade} students focusing on strong fundamentals and problem-solving.`,
    category: `Grade ${grade}`,
    image: 'https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=1200&q=60',
    topicsLabel: 'Topics',
    topics: getMathTopics(grade),
  };
});

// Sample course data
const computerScienceCourses: Course[] = [
  {
    id: 1,
    title: 'Python Programming for Beginners',
    description: 'Start coding with Python fundamentals and build confidence through beginner-friendly projects.',
    category: 'Python',
    image: 'https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?auto=format&fit=crop&w=1200&q=60',
    topicsLabel: 'Topics',
    topics: [
      'Variables, loops, functions',
      'Lists & dictionaries',
      'Simple algorithms',
    ],
    projects: [
      'Calculator',
      'Number guessing game',
      'Simple chatbot',
    ],
  },
  {
    id: 2,
    title: 'Web Development Basics',
    description: 'Learn how websites are built and publish your first pages with modern, foundational web tech.',
    category: 'Web Development',
    image: 'https://images.unsplash.com/photo-1547658719-da2b51169166?auto=format&fit=crop&w=1200&q=60',
    topicsLabel: 'Tech',
    topics: [
      'HTML, CSS, basic JavaScript',
    ],
    projects: [
      'Personal website',
      'School project site',
      'Simple interactive page',
    ],
  },
  {
    id: 3,
    title: 'Advanced Python + Problem Solving',
    description: 'Level up your Python skills while learning structured problem-solving and programming best practices.',
    category: 'Python',
    image: 'https://images.unsplash.com/photo-1515879218367-8466d910aaa4?auto=format&fit=crop&w=1200&q=60',
    topicsLabel: 'Topics',
    topics: [
      'File handling',
      'APIs',
      'Intro to OOP',
      'Competitive programming basics',
    ],
    projects: [
      'Student management system',
      'Weather app',
      'Quiz platform',
    ],
  },
  {
    id: 4,
    title: 'Full Web Development (Frontend)',
    description: 'Build real frontend applications and learn how modern websites are designed and developed.',
    category: 'Web Development',
    image: 'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1200&q=60',
    topicsLabel: 'Tech',
    topics: [
      'HTML, CSS',
      'JavaScript (ES6)',
      'React (basic)',
    ],
    projects: [
      'Portfolio website',
      'To-do app',
      'Blog website',
    ],
  },
  {
    id: 5,
    title: 'Data Science Foundations',
    description: 'Learn how to analyze data, create insights, and visualize results using Python data tools.',
    category: 'Data Science',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1200&q=60',
    topicsLabel: 'Tools',
    topics: [
      'Python',
      'Pandas',
      'Matplotlib',
    ],
    projects: [
      'Data analysis (sports or climate data)',
      'Charts & dashboards',
    ],
  },
  {
    id: 6,
    title: 'AI & Machine Learning (Beginner → Intermediate)',
    description: 'Understand AI fundamentals, build starter ML projects, and learn how to use AI tools responsibly.',
    category: 'AI / ML',
    image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&w=1200&q=60',
    topicsLabel: 'Topics',
    topics: [
      'Python for AI',
      'Machine learning basics',
      'AI tools usage',
      'Ethics of AI',
    ],
    projects: [
      'Movie recommender',
      'Face recognition (basic)',
      'Chatbot',
    ],
  },
];

const Courses = () => {
  const [selectedType, setSelectedType] = useState<'Computer Science' | 'Mathematics'>('Computer Science');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  const getCoursePath = (courseId: number) => {
    if (courseId === 1) {
      return '/courses/python-programming-for-beginners';
    }

    if (courseId === 5) {
      return '/courses/data-science-foundations';
    }

    if (courseId === 2) {
      return '/courses/web-development-basics';
    }

    return '/courses';
  };

  // Get unique categories
  const categories = ['All', ...new Set(computerScienceCourses.map(course => course.category))];

  // Filter courses based on search term, category, and level
  const filteredComputerScienceCourses = computerScienceCourses.filter(course => {
    const matchesSearch = course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         course.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || course.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <div className="bg-blue-700 pt-16 pb-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl font-extrabold tracking-tight text-white sm:text-5xl md:text-6xl">
            Our Courses
          </h1>
          <p className="mt-3 max-w-md mx-auto text-base text-blue-100 sm:text-lg md:mt-5 md:text-xl md:max-w-3xl">
            Explore our project-based coding tracks designed to build real skills.
          </p>
        </div>
      </div>

      {/* Search and Filter Section */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 -mt-12">
        <div className="bg-white rounded-lg shadow-lg p-6 mb-12">
          <div className="flex flex-wrap gap-3 mb-6">
            <button
              type="button"
              onClick={() => setSelectedType('Computer Science')}
              className={`px-4 py-2 rounded-md text-sm font-medium border ${selectedType === 'Computer Science' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
            >
              Computer Science
            </button>
            <button
              type="button"
              onClick={() => setSelectedType('Mathematics')}
              className={`px-4 py-2 rounded-md text-sm font-medium border ${selectedType === 'Mathematics' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
            >
              Mathematics
            </button>
          </div>

          {selectedType === 'Computer Science' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaSearch className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Search courses..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaFilter className="h-5 w-5 text-gray-400" />
                </div>
                <select
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm appearance-none"
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  {categories.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Courses Grid */}
        <div className="mb-16">
          {selectedType === 'Computer Science' ? (
            filteredComputerScienceCourses.length > 0 ? (
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {filteredComputerScienceCourses.map((course) => (
                <Link
                  key={course.id}
                  to={getCoursePath(course.id)}
                  className="flex flex-col rounded-lg shadow-lg overflow-hidden"
                >
                  <div className="flex-shrink-0">
                    <img 
                      className="h-48 w-full object-cover" 
                      src={course.image} 
                      alt={course.title} 
                    />
                  </div>
                  <div className="flex-1 bg-white p-6 flex flex-col justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-blue-600">
                        {course.category}
                      </p>
                      <div className="block mt-2">
                        <h3 className="text-xl font-semibold text-gray-900">
                          {course.title}
                        </h3>
                        <p className="mt-3 text-base text-gray-500">
                          {course.description}
                        </p>
                      </div>
                      <div className="mt-4">
                        <p className="text-sm font-semibold text-gray-900">{course.topicsLabel}</p>
                        <ul className="mt-2 space-y-1 text-sm text-gray-600">
                          {course.topics.map((topic: string) => (
                            <li key={topic}>{topic}</li>
                          ))}
                        </ul>
                      </div>
                      {course.projects && course.projects.length > 0 && (
                        <div className="mt-4">
                          <p className="text-sm font-semibold text-gray-900">Projects</p>
                          <ul className="mt-2 space-y-1 text-sm text-gray-600">
                            {course.projects.map((project: string) => (
                              <li key={project}>{project}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <h3 className="text-lg font-medium text-gray-900">No courses found</h3>
              <p className="mt-1 text-gray-500">Try adjusting your search or filter criteria.</p>
            </div>
            )
          ) : (
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {mathematicsCourses.map((course) => (
                <div key={course.id} className="flex flex-col rounded-lg shadow-lg overflow-hidden">
                  <div className="flex-1 bg-white p-6 flex flex-col justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-blue-600">
                        {course.category}
                      </p>
                      <div className="block mt-2">
                        <h3 className="text-xl font-semibold text-gray-900">
                          {course.title}
                        </h3>
                        <p className="mt-3 text-base text-gray-500">
                          {course.description}
                        </p>
                      </div>
                      <div className="mt-4">
                        <p className="text-sm font-semibold text-gray-900">{course.topicsLabel}</p>
                        <ul className="mt-2 space-y-1 text-sm text-gray-600">
                          {course.topics.map((topic: string) => (
                            <li key={topic}>{topic}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* CTA Section */}
        <div className="bg-blue-50 rounded-lg p-8 text-center mb-16">
          <h2 className="text-2xl font-bold text-gray-900">Can't find what you're looking for?</h2>
          <p className="mt-2 text-gray-600">
            We're always adding new courses. Let us know what you'd like to learn!
          </p>
          <div className="mt-6">
            <Link
              to="/contact"
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Courses;
